/**
 * Shopify App-URL entry handoff.
 *
 * A Shopify app (esp. once Embedded is ON for App Store review) is opened by
 * Shopify at its configured **App URL**. When that App URL is the bare canonical
 * host (`https://app.panluma.ai/?shop=…&hmac=…&host=…&timestamp=…`), CloudFront
 * serves it as the plain SPA — which would render the home page and drop the
 * Shopify params, dead-ending the install with no connection created.
 *
 * The backend already owns the entry logic at
 * `/api/v1/inventory/channels/shopify/install` (`channel_install_routes.py`):
 * HMAC-verify → 302 to OAuth consent for a fresh install, or render the embedded
 * App Home for an `?embedded=1` iframe load. That path is served by the ALB, not
 * the SPA, so this module simply hands the browser off to it — mirroring the
 * native-OAuth trampoline in `index.html` and `maybeRedirectToWorkspaceDomain`.
 *
 * See docs/modules/inventory/shopify-setup.md § App URLs.
 */
import { isCapacitor } from '../api/config';

/** Backend Shopify install entry (ALB-served — never the SPA, so no redirect loop). */
const SHOPIFY_INSTALL_PATH = '/api/v1/inventory/channels/shopify/install';

/**
 * A Shopify store handle host: `<handle>.myshopify.com`, anchored end-to-end so a
 * lookalike suffix (`…myshopify.com.evil.com`) never matches. Shopify normalizes
 * the `shop` param to this canonical form.
 */
const MYSHOPIFY_SHOP_RE = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

/**
 * The backend install URL to hand off to for a Shopify App-URL load, or `null`
 * when the current location is not one.
 *
 * Only the App URL root (`/`) carrying a genuine Shopify signature — a
 * `*.myshopify.com` `shop` plus the `hmac` Shopify signs every App-URL load with
 * — is handed off. The `hmac` requirement keeps this from being an open redirect
 * (a bare `?shop=` a user could craft is ignored). The query is forwarded
 * verbatim so the HMAC (computed over every other param) still verifies backend-side.
 */
export function shopifyAppEntryTarget(location: { pathname: string; search: string }): string | null {
  if (location.pathname !== '/') return null;
  const params = new URLSearchParams(location.search);
  const shop = (params.get('shop') ?? '').toLowerCase();
  if (!MYSHOPIFY_SHOP_RE.test(shop) || !params.get('hmac')) return null;
  return `${SHOPIFY_INSTALL_PATH}${location.search}`;
}

/**
 * If the current URL is a Shopify App-URL load, replace the location with the
 * backend install entry and return `true` (the caller should skip rendering the
 * SPA — the page is navigating away). Otherwise return `false`.
 *
 * No-op in Capacitor: Shopify never opens the bundled native shell, and a
 * relative `/api/...` replace there would resolve against `capacitor://localhost`.
 */
export function maybeRedirectToShopifyAppEntry(): boolean {
  if (isCapacitor) return false;
  const target = shopifyAppEntryTarget(window.location);
  if (!target) return false;
  window.location.replace(target);
  return true;
}
